import React from 'react';
import { fs, makeFields, FarAiAssist, Getter, Setter } from './farFields';

/* FAR Part 14 — Sealed Bidding (8 sections).
 * Shown when the CO-selected method is Part 14. Award is based on price and
 * price-related factors only, with no discussions. */

export default function Far14Form({ get, set }: { get: Getter; set: Setter }) {
  const F = makeFields(get, set);

  const suitabilityWarn = get('discussions_unnecessary') === 'No' || get('price_related_only') === 'No';

  return (
    <div>
      <div style={{ padding: '10px 14px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#854d0e' }}>FAR Part 14 — Sealed Bidding</span>
        <p style={{ fontSize: 11, color: '#a16207', margin: '2px 0 0' }}>Award based on price and price-related factors only. No discussions. Bids must be safeguarded and remain sealed until the public opening.</p>
      </div>
      <FarAiAssist formName="FAR Part 14 — Sealed Bidding" get={get} set={set}
        fields={[
          { key: 'co_rationale', label: 'Sealed-Bidding Suitability Rationale', kind: 'long' },
          { key: 'ifb_description', label: 'IFB Requirements Description', kind: 'long' },
        ]} />

      {/* Section 1 — Sealed-Bidding Suitability */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 1 · Sealed-Bidding Suitability</h3>
        {F.YN('time_available', 'Is There Time to Solicit, Submit, and Evaluate Sealed Bids?')}
        {F.YN('price_related_only', 'Will Award Be Based on Price and Price-Related Factors?')}
        {F.YN('discussions_unnecessary', 'Are Discussions With Bidders Unnecessary?')}
        {F.YN('multiple_bids_expected', 'Is There a Reasonable Expectation of More Than One Sealed Bid?')}
        {F.Sel('method', 'Selected Sealed-Bidding Method', ['One-step sealed bidding', 'Two-step sealed bidding'])}
        {F.Sel('suitability', 'Suitability Determination', ['Suitable', 'Not Suitable', 'Review Required'])}
        {F.Long('co_rationale', 'Contracting Officer Rationale')}
        {suitabilityWarn && (
          <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#991b1b' }}>
            Warning: Sealed bidding is not appropriate when discussions are needed or when non-price/tradeoff factors matter. Consider FAR Part 15 (negotiated acquisition).
          </div>
        )}
      </div>

      {/* Section 2 — Invitation for Bids Preparation */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 2 · Invitation for Bids (IFB) Preparation</h3>
        {F.Text('ifb_number', 'IFB Number')}
        {F.Long('ifb_description', 'Requirements Description')}
        {F.TableField('ifb_bid_schedule', 'Bid Schedule and Line Items', [
          { key: 'item', label: 'Item', type: 'text' },
          { key: 'description', label: 'Description', type: 'text' },
          { key: 'quantity', label: 'Quantity', type: 'text' },
          { key: 'unit', label: 'Unit', type: 'text' },
        ])}
        {F.Sel('ifb_contract_type', 'Contract Type', ['Firm-fixed-price', 'Fixed-price with economic price adjustment'])}
        {F.YN('ifb_bid_guarantee', 'Bid Guarantee Required?')}
        {F.YN('ifb_bonds', 'Performance or Payment Bonds Required?')}
        {F.ListField('ifb_price_related_factors', 'Price-Related Factors')}
        {F.Long('ifb_submission_instructions', 'Bid Submission Instructions')}
        {F.Text('ifb_opening', 'Bid Opening Date, Time, and Location')}
        {F.ListField('ifb_provisions_clauses', 'Applicable Provisions and Clauses')}
        {F.YN('ifb_complete', 'IFB Documents Complete?')}
      </div>

      {/* Section 3 — Publicizing and Amendments */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 3 · Publicizing and Amendments</h3>
        {F.YN('pub_publicized', 'IFB Publicized?')}
        {F.Date_('pub_date', 'Publicized Date')}
        {F.YN('pub_prep_period', 'Bid Preparation Period Sufficient?')}
        {F.YN('pub_bidders_notified', 'Potential Bidders Notified?')}
        {F.YN('pub_site_visit', 'Site Visit or Pre-Bid Conference Required?')}
        {F.YN('pub_amendments', 'Amendments Issued?')}
        {get('pub_amendments') === 'Yes' && F.TableField('pub_amendment_list', 'Amendments', [
          { key: 'amendment', label: 'Amendment', type: 'text' },
          { key: 'change', label: 'Change summary', type: 'long' },
          { key: 'issue_date', label: 'Issue date', type: 'text' },
          { key: 'ack_required', label: 'Acknowledgment required', type: 'select', options: ['Yes', 'No'] },
          { key: 'bidders_notified', label: 'Bidders notified', type: 'select', options: ['Yes', 'No'] },
        ])}
        {F.YN('pub_opening_postponed', 'Bid Opening Postponed?')}
        {get('pub_opening_postponed') === 'Yes' && F.Long('pub_postponement', 'Postponement Reason and New Opening Time')}
      </div>

      {/* Section 4 — Bid Receipt and Safeguarding */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 4 · Bid Receipt and Safeguarding</h3>
        <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 11, color: '#92400e', marginBottom: 12 }}>
          Bid prices must remain inaccessible until the official opening. Record receipts without disclosing prices.
        </div>
        {F.TableField('bid_register', 'Secure Bid Register', [
          { key: 'bidder', label: 'Bidder', type: 'text' },
          { key: 'receipt_time', label: 'Receipt time', type: 'text' },
          { key: 'method', label: 'Submission method', type: 'text' },
          { key: 'sealed', label: 'Sealed/intact', type: 'select', options: ['Yes', 'No'] },
          { key: 'late', label: 'Late?', type: 'select', options: ['Yes', 'No'] },
          { key: 'safeguarding', label: 'Safeguarding status', type: 'text' },
        ])}
        {F.Text('bid_custodian', 'Bid Custodian')}
        {F.YN('late_bid_review', 'Late Bid Review Required?')}
        {get('late_bid_review') === 'Yes' && <>
          {F.Sel('late_bid_determination', 'Late Bid Determination', ['Accept', 'Reject', 'Pending Legal Review'])}
          {F.Long('late_bid_rationale', 'Late Bid Rationale')}
        </>}
      </div>

      {/* Section 5 — Public Bid Opening */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 5 · Public Bid Opening</h3>
        {F.Text('open_datetime', 'Actual Opening Date and Time')}
        {F.Text('open_official', 'Opening Official')}
        {F.ListField('open_witnesses', 'Witnesses or Attendees')}
        {F.YN('open_public', 'Bids Publicly Opened?')}
        {F.TableField('open_abstract', 'Bid Abstract', [
          { key: 'bidder', label: 'Bidder', type: 'text' },
          { key: 'base_bid', label: 'Base bid', type: 'text' },
          { key: 'options', label: 'Options', type: 'text' },
          { key: 'adjustments', label: 'Price-related adjustments', type: 'text' },
          { key: 'evaluated_total', label: 'Evaluated total', type: 'text' },
        ])}
        {F.Text('open_record', 'Bid Opening Record (reference)')}
      </div>

      {/* Section 6 — Responsiveness and Responsibility */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 6 · Responsiveness and Responsibility</h3>
        <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 11, color: '#92400e', marginBottom: 12 }}>
          No discussions may be used to permit a bidder to correct a material nonresponsive bid.
        </div>
        {F.TableField('resp_table', 'Responsiveness & Responsibility', [
          { key: 'bidder', label: 'Bidder', type: 'text' },
          { key: 'signed', label: 'Bid signed?', type: 'select', options: ['Yes', 'No'] },
          { key: 'ack', label: 'Amendments acknowledged?', type: 'select', options: ['Yes', 'No'] },
          { key: 'guarantee', label: 'Bid guarantee acceptable?', type: 'select', options: ['Yes', 'No', 'N/A'] },
          { key: 'conforms', label: 'Conforms to material IFB terms?', type: 'select', options: ['Yes', 'No'] },
          { key: 'responsive', label: 'Bid responsive?', type: 'select', options: ['Yes', 'No'] },
          { key: 'responsible', label: 'Bidder responsible?', type: 'select', options: ['Yes', 'No', 'Pending'] },
          { key: 'price', label: 'Evaluated price', type: 'text' },
          { key: 'rejection', label: 'Rejection required?', type: 'select', options: ['Yes', 'No'] },
          { key: 'rejection_rationale', label: 'Rejection rationale', type: 'long' },
        ])}
      </div>

      {/* Section 7 — Award Decision */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 7 · Award Decision</h3>
        {F.Text('award_low_bidder', 'Lowest Responsive, Responsible Bidder (system recommendation)')}
        {F.Text('award_selected', 'Selected Bidder')}
        {F.YN('award_differs', 'Does the Selection Differ From the System Recommendation?')}
        {get('award_differs') === 'Yes' && F.Long('award_rationale', 'Selection Rationale')}
        {F.YN('award_price_reasonable', 'Price Reasonable?')}
        {F.YN('award_equal_low', 'Equal Low Bids?')}
        {get('award_equal_low') === 'Yes' && F.Long('award_tiebreak', 'Tie-Breaking Procedure')}
        {F.YN('award_cancellation', 'IFB Cancellation Required?')}
        {get('award_cancellation') === 'Yes' && F.Long('award_cancel_rationale', 'Cancellation or Rejection Rationale')}
        {F.YN('award_doc_complete', 'Award Documentation Complete?')}
        {F.YN('award_bidders_notified', 'Bidders Notified?')}
      </div>

      {/* Section 8 — Final Review */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 8 · FAR Part 14 Final Review</h3>
        {F.Checklist('final_review', [
          'Conditions for sealed bidding were satisfied.',
          'IFB clearly and completely described the requirement.',
          'IFB was properly publicized.',
          'All bidders received amendments.',
          'Bids were safeguarded before opening.',
          'Bids were opened at the stated time.',
          'Bid opening was properly recorded.',
          'Late bids received an appropriate determination.',
          'Responsiveness was evaluated without discussions.',
          'Bidder responsibility was verified.',
          'Only stated price-related factors were considered.',
          'Award was made to the responsive, responsible bidder offering the most advantageous evaluated price.',
          'Rejections or cancellation were documented.',
          'Award record is complete.',
        ])}
      </div>
    </div>
  );
}
